import React, { useState } from 'react';
import { Button, ButtonGroup, Text } from '@fragments';
import { IconArrowRight, IconArrowUpRight, IconSparkles } from '@tabler/icons-react';
import { formatCredits, formatDay, formatMoney } from '../../../../data/payments';
import './styles.scss';

// The balance (plan allowance spent first, then credits that never expire), credit
// packs, and — on Standard — a second part offering Pro. Rendered inside the Popper.
export default function CreditsPanel({ studio: s, id, panelRef, primaryRef, onKeyDown, onBuy, onUpgrade }) {
  const { t } = s;
  const p = s.payments;
  const locale = s.locale.replace('_', '-');
  const presets = p.pack?.presets || [];
  const [quantity, setQuantity] = useState(presets[1] ?? presets[0] ?? p.pack?.min_quantity);
  const usage = s.snapshot.usage || {};
  const available = usage.available_units ?? 0;
  const allowance = usage.allowance_units ?? 0;
  const purchased = usage.purchased_units ?? Math.max(available - allowance, 0);
  const count = units => formatCredits(units, locale);
  const pro = p.planInfo('pro');
  const total = p.packPrice === null || !quantity ? null : quantity * p.packPrice;
  return <div ref={panelRef} id={id} role="dialog" aria-labelledby={`${id}-title`} className="studio-credits-panel" onKeyDown={onKeyDown}>
    <section className="studio-credits-panel__balance">
      <div className="studio-credits-panel__row">
        <Text.caption id={`${id}-title`} contrast={6}>{t('creditsBalance')}</Text.caption>
        <Text.h4 contrast={9} weight={500}>{count(available)}</Text.h4>
      </div>
      {p.plan !== 'free' && <>
        <div className="studio-credits-panel__split" aria-hidden="true">
          <i className="is-plan" style={{ flexGrow: allowance }} />
          <i className="is-pack" style={{ flexGrow: purchased }} />
        </div>
        <ul className="studio-credits-panel__legend">
          <li><i className="is-plan" aria-hidden="true" /><Text.caption contrast={7}>{t('creditsPlanShare', { plan: t(`planName_${p.plan}`) })}</Text.caption><Text.caption contrast={9}>{count(allowance)}</Text.caption></li>
          <li><i className="is-pack" aria-hidden="true" /><Text.caption contrast={7}>{t('creditsPackShare')}</Text.caption><Text.caption contrast={9}>{count(purchased)}</Text.caption></li>
        </ul>
        {usage.allowance_expires_at && <Text.caption contrast={5}>{t('creditsRenews', { date: formatDay(usage.allowance_expires_at, locale) })}</Text.caption>}
      </>}
    </section>
    {p.pack && <section className="studio-credits-panel__buy">
      <div className="studio-credits-panel__heading">
        <Text.label contrast={9}>{t('creditsBuyTitle')}</Text.label>
        <Text.caption contrast={6}>{t('creditsBuyHint')}</Text.caption>
      </div>
      <ButtonGroup size="small" grow options={presets.map(value => ({ value, label: count(value * p.pack.credits_per_pack) }))} value={quantity} onChange={setQuantity} />
      <div className="studio-credits-panel__row">
        <Text.span contrast={8}>{formatMoney(total, p.currency, locale)}</Text.span>
        <Button ref={primaryRef} size="small" variant="rounded" color="inverse" preventLoading endIcon={<IconArrowRight />} onClick={() => onBuy(quantity)}>{t('creditsBuy')}</Button>
      </div>
    </section>}
    {p.plan === 'standard' && pro && <section className="studio-credits-panel__upgrade">
      <span className="studio-credits-panel__glow" aria-hidden="true" />
      <Text.caption contrast={8} left={<IconSparkles />}>{t('planName_pro')}</Text.caption>
      <Text.p contrast={7}>{t('creditsUpgradeText', { credits: count(pro.monthly_credits) })}</Text.p>
      <Button size="small" fullWidth variant="rounded" color="overlay" preventLoading endIcon={<IconArrowUpRight />} onClick={onUpgrade}>{t('creditsUpgradeCta')}</Button>
    </section>}
  </div>;
}
